// Widget_UpcomingMeetings — task 5.4 of the dashboard-redesign spec (Req 10).
//
// Read-only list of the next up-to-`limit` upcoming meetings in the current
// `BuildingScope`. Consumes `useUpcomingMeetings(scope, days=60)` (task 2.6)
// which returns rows already sorted ascending by `daysUntilStart`. This
// widget owns:
//
//   1. State-aware chrome via <WidgetFrame> — the 4 lifecycle states share
//      one bounding box so the surrounding page never reflows (Req 3.5).
//   2. Ready-state layout — meeting title (bold), building (muted), date
//      column ("Today" / "Tomorrow" / a formatted `en-GB` short date), and
//      an optional type badge (AGM / Directors / Other) when the endpoint
//      supplies enough context to derive one.
//   3. Deep-links — each row navigates to `/dashboard/meetings/{id}`
//      (`MeetingDetails` route in `frontend/src/App.tsx`) and, when the
//      hook returns more than `limit` rows, a widget-level footer link to
//      `/dashboard/meetings` shows "View all N meetings →" so a treasurer
//      can jump to the full list.
//   4. Defensive rendering — the endpoint's payload shape is owned by the
//      `meetings` spec and can evolve independently. Fields beyond the
//      shared `Meeting` interface (`scheduledFor`, `buildingName`, `type`)
//      are accessed via a narrow `unknown` cast so a missing/renamed field
//      degrades to a `—` cell instead of crashing the widget.
//
// The whole tree is wrapped in <ErrorBoundary> so a render-time exception
// in this widget cannot blank the surrounding dashboard (Req 3.6, P4).
// No `dangerouslySetInnerHTML`, no `any`, strict-mode compliant.

import React from "react";
import { Link } from "react-router-dom";
import { ErrorBoundary } from "../../../components/ui/ErrorBoundary";
import { WidgetFrame } from "../WidgetFrame";
import { WidgetError } from "../WidgetError";
import { useUpcomingMeetings } from "../hooks/useUpcomingMeetings";
import type { BuildingScope, Meeting, UpcomingMeetingRow } from "../types";

/** Default row cap when the caller doesn't override it (Req 10). */
const DEFAULT_LIMIT = 5;

/** Human-readable widget name — surfaced in error card + section header. */
const WIDGET_NAME = "Upcoming Meetings";

/** Widget-level "view everything" link — the meetings list route. */
const MEETINGS_LIST_HREF = "/dashboard/meetings";

/** Fixed copy for the empty state — shown when the hook returns `empty`. */
const EMPTY_COPY = "No upcoming meetings";

/**
 * Shared date formatter for the row's date column. Instantiated once at
 * module load so we don't spin up a new `Intl.DateTimeFormat` per row —
 * the constructor is comparatively expensive and the options are constant.
 */
const DATE_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
  month: "short",
});

/** Narrow view over the extra endpoint fields we defensively read. */
interface MeetingExtras {
  scheduledFor?: unknown;
  buildingName?: unknown;
  buildingId?: unknown;
  title?: unknown;
  type?: unknown;
  regulatorySchedule?: { category?: unknown } | null | undefined;
}

/**
 * Read `scheduledFor` off the endpoint payload. The shared `Meeting` model
 * uses `date`, but the meetings-upcoming endpoint (owned by the `meetings`
 * spec) returns `scheduledFor` — the hook types the passed-through row as
 * `Meeting` for consumer stability, but we still need the ISO string here.
 * Returns `null` if the field is missing or empty.
 */
function readScheduledFor(meeting: Meeting): string | null {
  const m = meeting as unknown as MeetingExtras;
  return typeof m.scheduledFor === "string" && m.scheduledFor.length > 0
    ? m.scheduledFor
    : null;
}

/**
 * Read the meeting's building label. Prefers a `buildingName` field when
 * the endpoint supplies one (some dashboard-facing endpoints denormalise
 * this to avoid a second round-trip), and falls back to `buildingId` so
 * the row always carries some building context. Empty string → the row
 * renders `—` instead.
 */
function readBuildingLabel(meeting: Meeting): string {
  const m = meeting as unknown as MeetingExtras;
  if (typeof m.buildingName === "string" && m.buildingName.length > 0) {
    return m.buildingName;
  }
  if (typeof m.buildingId === "string" && m.buildingId.length > 0) {
    return m.buildingId;
  }
  return "";
}

/**
 * Read the meeting's title. Falls back to a generic label when absent so
 * the row still renders (and the deep-link still works via `meeting.id`).
 */
function readTitle(meeting: Meeting): string {
  const m = meeting as unknown as MeetingExtras;
  if (typeof m.title === "string" && m.title.length > 0) return m.title;
  return "Untitled meeting";
}

/**
 * Derive a badge label from either an explicit `type` field on the row or
 * `regulatorySchedule.category` from the shared `Meeting`. Returns `null`
 * when no type can be derived (the row then hides the badge slot entirely
 * — we never emit an empty pill).
 */
function readMeetingType(meeting: Meeting): string | null {
  const m = meeting as unknown as MeetingExtras;
  if (typeof m.type === "string" && m.type.length > 0) return m.type;
  const category = m.regulatorySchedule?.category;
  if (typeof category === "string" && category.length > 0) return category;
  return null;
}

/**
 * Map a raw type/category string to its badge display label. Anything we
 * don't recognise renders as `"Other"` so the pill copy never leaks
 * kebab-case endpoint tokens into the UI.
 */
function formatMeetingTypeLabel(raw: string): string {
  const lower = raw.toLowerCase();
  switch (lower) {
    case "agm":
      return "AGM";
    case "directors":
      return "Directors";
    case "service-charge":
      return "Service Charge";
    case "accounts":
      return "Accounts";
    case "custom":
    case "other":
      return "Other";
    default:
      return "Other";
  }
}

/**
 * Format the date column. Uses the row's `daysUntilStart` (already
 * computed by the hook) for the "Today" / "Tomorrow" shortcuts, then
 * falls back to a formatted `en-GB` short date (e.g. `Mon 12 Oct`) via
 * the shared `DATE_FORMATTER`. Falls back to `—` when `scheduledFor`
 * is unparsable — should be rare because the hook already validated it.
 */
function formatDateColumn(row: UpcomingMeetingRow): string {
  if (row.daysUntilStart === 0) return "Today";
  if (row.daysUntilStart === 1) return "Tomorrow";
  const iso = readScheduledFor(row.meeting);
  if (!iso) return "—";
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return "—";
  return DATE_FORMATTER.format(new Date(parsed));
}

export interface WidgetUpcomingMeetingsProps {
  scope: BuildingScope;
  /** Max rows to render in the list. Defaults to 5 (Req 10). */
  limit?: number;
}

/**
 * Widget chrome — a white rounded card with a header. Kept as a local
 * component so every render state (skeleton / empty / ready / error)
 * shares the same bounding box and the surrounding grid never reflows.
 */
function TileFrame({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      data-testid="widget-upcoming-meetings"
      className="bg-white rounded-xl border h-full flex flex-col p-4"
      style={{ borderColor: "#e5e7eb" }}
    >
      <div
        className="text-sm font-semibold flex items-center justify-between"
        style={{ color: "#111827" }}
      >
        <span>{WIDGET_NAME}</span>
      </div>
      <div className="mt-2 border-t" style={{ borderColor: "#e5e7eb" }} />
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

/**
 * A single row in the list. Wrapped in a `<Link>` so the entire row is a
 * deep-link target — matches the touch-target guidance in the design
 * language and keeps the click affordance obvious.
 */
function MeetingRow({ row }: { row: UpcomingMeetingRow }): JSX.Element {
  const meeting = row.meeting;
  const title = readTitle(meeting);
  const building = readBuildingLabel(meeting);
  const dateLabel = formatDateColumn(row);
  const rawType = readMeetingType(meeting);
  const typeLabel = rawType !== null ? formatMeetingTypeLabel(rawType) : null;

  return (
    <Link
      to={`/dashboard/meetings/${encodeURIComponent(meeting.id)}`}
      data-testid={`upcoming-meeting-row-${meeting.id}`}
      className="flex items-center justify-between gap-3 py-2 px-1 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <div className="min-w-0 flex-1">
        <div
          className="text-sm font-semibold truncate"
          style={{ color: "#111827" }}
          data-testid={`upcoming-meeting-title-${meeting.id}`}
        >
          {title}
        </div>
        <div
          className="text-xs truncate"
          style={{ color: "#6b7280" }}
          data-testid={`upcoming-meeting-building-${meeting.id}`}
        >
          {building.length > 0 ? building : "—"}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {typeLabel && (
          <span
            data-testid={`upcoming-meeting-type-${meeting.id}`}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: "#f3f4f6", color: "#374151" }}
          >
            {typeLabel}
          </span>
        )}
        <span
          data-testid={`upcoming-meeting-date-${meeting.id}`}
          className="text-xs font-medium tabular-nums"
          style={{ color: "#374151" }}
        >
          {dateLabel}
        </span>
      </div>
    </Link>
  );
}

/**
 * Ready-state body. Renders up to `limit` rows and, when the hook
 * returned more than `limit`, a footer "View all N meetings →" link.
 */
function UpcomingMeetingsReady({
  rows,
  limit,
}: {
  rows: UpcomingMeetingRow[];
  limit: number;
}): JSX.Element {
  const visibleRows = rows.slice(0, limit);
  const hasMore = rows.length > limit;

  return (
    <div className="flex flex-col h-full">
      <ul
        className="flex flex-col divide-y"
        style={{ borderColor: "#e5e7eb" }}
        data-testid="upcoming-meetings-list"
      >
        {visibleRows.map((row) => (
          <li key={row.meeting.id}>
            <MeetingRow row={row} />
          </li>
        ))}
      </ul>
      {hasMore && (
        <div className="mt-2 pt-2 border-t" style={{ borderColor: "#e5e7eb" }}>
          <Link
            to={MEETINGS_LIST_HREF}
            data-testid="upcoming-meetings-view-all-link"
            className="text-xs hover:underline"
            style={{ color: "#2563eb" }}
          >
            View all {rows.length} meetings →
          </Link>
        </div>
      )}
    </div>
  );
}

/**
 * Inner component that consumes the hook. Kept as a separate function so
 * the hook call sits INSIDE the <ErrorBoundary> — a hook that throws
 * during render (e.g. an upstream module bug) is then caught by the
 * boundary instead of propagating into the surrounding dashboard.
 * Mirrors the pattern used by `Widget_Arrears`.
 */
function WidgetUpcomingMeetingsInner({
  scope,
  limit,
}: {
  scope: BuildingScope;
  limit: number;
}): JSX.Element {
  const result = useUpcomingMeetings(scope);

  return (
    <WidgetFrame
      result={result}
      name={WIDGET_NAME}
      emptyCopy={EMPTY_COPY}
      skeletonHeight={140}
    >
      {result.status === "ready" && (
        <UpcomingMeetingsReady rows={result.data} limit={limit} />
      )}
    </WidgetFrame>
  );
}

/**
 * Public widget. Wraps the hook-consuming inner tree in an
 * <ErrorBoundary> for defence-in-depth so a render-time exception in
 * this widget's subtree cannot blank the surrounding dashboard
 * (Requirement 3.6, Property P4).
 */
export function Widget_UpcomingMeetings({
  scope,
  limit = DEFAULT_LIMIT,
}: WidgetUpcomingMeetingsProps): JSX.Element {
  return (
    <ErrorBoundary
      fallback={
        <TileFrame>
          <WidgetError name={WIDGET_NAME} errorId="error-boundary" />
        </TileFrame>
      }
    >
      <TileFrame>
        <WidgetUpcomingMeetingsInner scope={scope} limit={limit} />
      </TileFrame>
    </ErrorBoundary>
  );
}

export default Widget_UpcomingMeetings;
